import type { Lobby, LobbyEvent, LobbyParty } from '@danda/shared';
import type { CharacterPermanent, CharacterSession } from '@danda/shared';
import { ActiveNPCEnum, RoomTypeEnum } from '@danda/shared';
import type { Character } from '../lobbyStore.types';
import type { EventHandlerContext } from './lobby-event-types';
import { getPersistedActiveChatRoom, isSafeToRestoreRoom } from '../lobbyStore.chat';
import { usePmBalanceStore } from '../pmBalanceStore';
import { websocketService } from '@/lib/websocket-service';

/** Rate-limit portrait regeneration requests per character (max once per 5 minutes) */
const PORTRAIT_REGEN_COOLDOWN_MS = 5 * 60 * 1000;
const portraitRequestTimes = new Map<string, number>();

import type { LobbyState } from '../lobbyStore.types';

/** Validate a string field: non-empty string or undefined */
function validStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/** Sync character data from session-state event */
function syncCharacterData(
  state: LobbyState,
  charId: string,
  characterData: { permanent: CharacterPermanent; session?: CharacterSession }
) {
  const permanent = characterData.permanent;
  const charName = validStr(permanent.name) ?? 'Unknown';
  // avatar_url lives on session only — see commit ae8ad8392 rationale.
  // Reading from permanent here would resurrect a stale Firestore field.
  const avatarUrl = validStr(characterData.session?.avatar_url ?? characterData.session?.avatarUrl);
  const avatarHash = validStr(
    characterData.session?.avatar_hash ?? characterData.session?.avatarHash
  );

  const sessionPortraitUrl =
    validStr(characterData.session?.portrait_url ?? characterData.session?.portraitUrl) ?? null;

  const existingChar = state.characters.find((c) => c.id === charId);
  if (!existingChar) {
    state.addCharacter({
      id: charId,
      name: charName,
      permanent,
      session: characterData.session,
      avatar: avatarUrl,
      avatarUrl,
      avatarHash,
      avatarEmoji: permanent.avatar_emoji,
      portraitUrl: sessionPortraitUrl ?? undefined,
    });
    console.log('[LobbyStore] Added character from session state', { charId, charName });
  } else {
    const updates: Partial<Character> = { permanent, session: characterData.session };
    if (!existingChar.avatarEmoji && permanent.avatar_emoji) {
      updates.avatarEmoji = permanent.avatar_emoji;
    }
    if (avatarUrl && existingChar.avatarUrl !== avatarUrl) {
      updates.avatarUrl = avatarUrl;
      updates.avatar = avatarUrl;
      updates.avatarHash = avatarHash;
    }
    if (sessionPortraitUrl && existingChar.portraitUrl !== sessionPortraitUrl) {
      updates.portraitUrl = sessionPortraitUrl;
    }
    state.updateCharacter(charId, updates);
  }

  state.selectCharacter(charId);
  state.setCharCreationCharId(null);

  // Inner Voice room — add/swap on character select
  const innerRoomId = `inner-${charId}`;
  // Remove any existing inner voice rooms for other characters (character swap)
  const staleInnerRooms = state.chatRooms.filter((r) => r.type === 'inner' && r.id !== innerRoomId);
  for (const room of staleInnerRooms) {
    state.removeChatRoom(room.id);
  }
  // Check if target room already exists (read from snapshot — removals above only touch OTHER
  // inner rooms, so our target room's presence in the snapshot is still accurate)
  const hasInnerRoom = state.chatRooms.some((r) => r.id === innerRoomId);
  // Create the inner voice room if it doesn't exist and send welcome trigger
  if (!hasInnerRoom) {
    state.addChatRoom({
      id: innerRoomId,
      name: charName,
      type: 'inner',
      participants: [charId],
      messages: [],
      unreadCount: 0,
      avatarUrl,
      avatarEmoji: permanent.avatar_emoji,
      portraitUrl: sessionPortraitUrl ?? undefined,
    });
    // Send welcome trigger (fire-and-forget). This goes through sendChatMessage which sets
    // isWaitingForResponse briefly — acceptable since the stream-end always clears it.
    setTimeout(async () => {
      try {
        const { default: ws } = await import('../../websocket-service');
        ws.sendChatMessage(innerRoomId, '[WELCOME]');
      } catch (e) {
        console.warn('[LobbyStore] Failed to send inner voice welcome:', e);
      }
    }, 500);
  }

  // Sync portrait URL from session (sessionPortraitUrl already resolved above for inner voice room)
  if (sessionPortraitUrl !== state.characterPortraitUrl) {
    state.setCharacterPortrait(sessionPortraitUrl, sessionPortraitUrl ? 'image' : null);
  }

  // Auto-request portrait regeneration if avatar URL is missing (rate-limited)
  if (!avatarUrl) {
    const now = Date.now();
    const lastRequest = portraitRequestTimes.get(charId) ?? 0;
    if (now - lastRequest >= PORTRAIT_REGEN_COOLDOWN_MS) {
      portraitRequestTimes.set(charId, now);
      console.log('[LobbyStore] Avatar URL missing, requesting portrait regeneration', {
        charId,
        charName,
      });
      setTimeout(async () => {
        try {
          const { default: ws } = await import('../../websocket-service');
          ws.requestPortrait(charId);
        } catch (e) {
          console.warn('[LobbyStore] Failed to request portrait regeneration:', e);
        }
      }, 500);
    }
  }
}

export function handleSessionEvents(event: LobbyEvent, ctx: EventHandlerContext): boolean {
  const { get } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:server-info': {
      const storedInstanceId = sessionStorage.getItem('lobby-server-instance-id');
      const serverChanged = storedInstanceId && storedInstanceId !== event.serverInstanceId;
      if (serverChanged && state.mainChatNpc !== ActiveNPCEnum.TAVIAN) {
        console.log('[LobbyStore] Server restarted, clearing NPC session state', {
          oldInstanceId: storedInstanceId,
          newInstanceId: event.serverInstanceId,
          wasNpc: state.mainChatNpc,
        });
        state.returnToTavian();
        state.setCharCreationAvailable(false);
        state.clearChatHistory('main');
      }
      sessionStorage.setItem('lobby-server-instance-id', event.serverInstanceId);
      return true;
    }

    case 'lobby:updated':
      if ((event.lobby as Lobby & { parties?: LobbyParty[] }).parties) {
        const lobby = event.lobby as Lobby & { parties?: LobbyParty[] };
        state.setParties(lobby.parties ?? []);
        // Note: Party chat room creation and switching happens in lobby:session-state
        // because selectedCharacterId isn't set yet when lobby:updated fires on initial load
        const userCharacterId = state.selectedCharacterId;
        if (userCharacterId) {
          const userParty = (lobby.parties ?? []).find((p) =>
            p.memberCharacterIds.includes(userCharacterId)
          );
          if (userParty) {
            state.setCurrentParty(userParty.id);
            const partyRoomId = `party-${userParty.id}`;
            const existingRoom = state.chatRooms.find((r) => r.id === partyRoomId);
            if (existingRoom) {
              // Update participants if room exists (e.g., party membership changed)
              state.updateChatRoomParticipants(partyRoomId, userParty.memberCharacterIds);
            }
          }
        }
      }
      // Mark lobby:updated as received and check if both initialization events are ready
      state.setLobbyUpdatedReceived(true);
      if (state.sessionStateReceived) {
        state.setInitialized(true);
      }
      return true;

    case 'lobby:session-state': {
      const sessionEvent = event as unknown as {
        type: 'lobby:session-state';
        activeNpc: 'tavian' | 'aldric' | 'kira';
        sessionId?: string;
        charCreationInProgress?: boolean;
        charId?: string;
        levelUpInProgress?: boolean;
        characterData?: { permanent: CharacterPermanent; session?: CharacterSession };
        partyId?: string | null;
        partyName?: string;
      };

      // Reset the post-session typing dots on every session-state sync (connect
      // / reconnect / re-init). party-typing is a transient true/false bracket
      // from the recovery flow; if the lobby WS dropped after a `true` but
      // before the `false`, the flag would otherwise stay set forever. A
      // reconnect that lands mid-recovery loses the dots (the persisted
      // recovery-narration replay still delivers the content) — strictly better
      // than dots stuck on indefinitely.
      if (Object.keys(state.partyTyping).length > 0) {
        ctx.set({ partyTyping: {} });
      }

      // Server is source of truth for NPC state
      // Always sync mainChatNpc from server, but only call switchMainNpc (which adds
      // welcome messages and updates room) when room type actually differs
      const mainRoom = state.chatRooms.find((r) => r.id === 'main');
      const currentMainRoomType = mainRoom?.type ?? 'tavian';

      // Always set mainChatNpc to match server (it's not persisted, defaults to 'tavian')
      if (state.mainChatNpc !== sessionEvent.activeNpc) {
        state.setMainChatNpc(sessionEvent.activeNpc);
      }

      // Only switch room (with welcome message) if room type differs from server
      if (sessionEvent.activeNpc !== currentMainRoomType) {
        console.log('[LobbyStore] Syncing NPC room state from backend', {
          had: currentMainRoomType,
          got: sessionEvent.activeNpc,
        });
        state.switchMainNpc(sessionEvent.activeNpc);
      }

      const serverPartyId = sessionEvent.partyId ?? null;

      // Clean up orphan party rooms with placeholder names (from stale state)
      const orphanRooms = state.chatRooms.filter(
        (r) => r.type === RoomTypeEnum.PARTY && r.name === 'Party Chat'
      );
      for (const room of orphanRooms) {
        console.log('[LobbyStore] Removing orphan party room', { roomId: room.id });
        state.removeChatRoom(room.id);
      }

      // Sweep stale party rooms — anything not matching the server's current party.
      // currentPartyId is not persisted, so on reload the 238–241 guard below can't
      // detect mismatch; this catches party rooms left in localStorage from past
      // parties (and parties the user was removed from while offline).
      const currentPartyRoomId = serverPartyId ? `party-${serverPartyId}` : null;
      const stalePartyRooms = state.chatRooms.filter(
        (r) => r.type === RoomTypeEnum.PARTY && r.id !== currentPartyRoomId
      );
      for (const room of stalePartyRooms) {
        console.log('[LobbyStore] Removing stale party room', {
          roomId: room.id,
          serverPartyId,
        });
        state.removeChatRoom(room.id);
      }

      if (serverPartyId !== state.currentPartyId) {
        console.log('[LobbyStore] Syncing party state from backend', {
          had: state.currentPartyId,
          got: serverPartyId,
        });
        if (serverPartyId === null && state.currentPartyId) {
          const oldPartyRoomId = `party-${state.currentPartyId}`;
          state.removeChatRoom(oldPartyRoomId);
          state.setCurrentParty(null);
        } else if (serverPartyId) {
          if (state.currentPartyId && state.currentPartyId !== serverPartyId) {
            const oldPartyRoomId = `party-${state.currentPartyId}`;
            state.removeChatRoom(oldPartyRoomId);
          }
          state.setCurrentParty(serverPartyId);
        }
      }

      // Clear any stale decisions on reconnect
      // Server is source of truth - if there's an active decision, server will send
      // lobby:party-decision-proposed event. This prevents showing old decisions that
      // already passed/failed while user was disconnected.
      // Clear decisions from ALL parties (not just current) to handle party switches
      const allPartyIds = Object.keys(state.partyDecisions);
      for (const partyId of allPartyIds) {
        const existingDecision = state.partyDecisions[partyId];
        if (existingDecision) {
          console.log('[LobbyStore] Clearing stale decision on session sync', {
            partyId,
            decisionId: existingDecision.id,
            decisionType: existingDecision.type,
            status: existingDecision.status,
            isCurrentParty: partyId === serverPartyId,
          });
          state.clearPartyDecision(partyId);
        }
      }

      // Clear any stale pending invites on reconnect, same reasoning as the
      // decisions clear above: the server now replays every CURRENTLY valid
      // pending invite right after this session-state message
      // (handle_lobby_join), so this is authoritative. Without the clear, an
      // invite that was accepted/declined/expired while the socket was down
      // would keep showing a now-dead banner (the server has no other way to
      // push a removal for something the client never got, since a lost
      // PartyInviteRemoved has no retry).
      if (state.pendingInvites.length > 0) {
        state.clearPendingInvites();
      }

      // Always ensure party room exists with correct data when we have a party
      if (serverPartyId) {
        const partyRoomId = `party-${serverPartyId}`;
        const existingRoom = state.chatRooms.find((r) => r.id === partyRoomId);
        // Use partyName from event (most reliable) or fallback to state lookup
        const party = state.parties.find((p) => p.id === serverPartyId);
        const partyName = sessionEvent.partyName ?? party?.name ?? 'Party Chat';
        const participants = party?.memberCharacterIds ?? [];

        if (!existingRoom) {
          // Create party room - welcome message is handled by background-events.ts
          // when lobby:background-generated event fires
          state.addChatRoom({
            id: partyRoomId,
            name: partyName,
            participants,
            messages: [],
            unreadCount: 0,
            type: 'party',
            isTavianPresent: true,
          });
        } else if (
          existingRoom.name !== partyName ||
          existingRoom.participants.length !== participants.length ||
          existingRoom.participants.some((id, idx) => id !== participants[idx])
        ) {
          // Update existing room if name or participants changed
          state.updateChatRoom(partyRoomId, { name: partyName, participants });
        }

        // Restore the user's last active chat tab if it still exists, otherwise default to party chat.
        // Read chatRooms fresh from the store — `state` is a snapshot captured at handler entry,
        // so `state.chatRooms` still contains rooms the sweep removed above. Without re-reading,
        // a savedRoomId pointing at a just-swept stale party (e.g. "party-old") would pass the
        // some() check and switchChatRoom would silently no-op, stranding the user on `main`.
        const savedRoomId = getPersistedActiveChatRoom();
        const liveRooms = get().chatRooms;
        const restoreRoomId =
          savedRoomId &&
          isSafeToRestoreRoom(savedRoomId) &&
          liveRooms.some((r) => r.id === savedRoomId)
            ? savedRoomId
            : partyRoomId;
        console.log('[LobbyStore] Restoring chat tab on session restore', {
          partyId: serverPartyId,
          partyName,
          participants,
          hadPartyInfo: !!party,
          savedRoomId,
          restoredTo: restoreRoomId,
        });
        state.switchChatRoom(restoreRoomId);
      }

      if (sessionEvent.charId && sessionEvent.characterData?.permanent) {
        syncCharacterData(state, sessionEvent.charId, sessionEvent.characterData);
      } else if (sessionEvent.charId && !sessionEvent.characterData?.permanent) {
        // Character creation in progress - store charId for use in sendAldricMessage
        console.log('[LobbyStore] Storing charId for character creation in progress', {
          charId: sessionEvent.charId,
        });
        state.setCharCreationCharId(sessionEvent.charId);
        // Clear selected character if any (since char creation is in progress)
        if (state.selectedCharacterId) {
          state.selectCharacter(null);
        }
      } else if (state.selectedCharacterId) {
        // Server says no character - clear any stale selection
        console.log('[LobbyStore] Clearing stale character selection', {
          staleCharacterId: state.selectedCharacterId,
          reason: 'no character on server',
        });
        state.selectCharacter(null);
        state.setCharCreationCharId(null);
      }

      // Resume an in-flight Aldric creation whose opener never reached THIS
      // client. On a dashboard->lobby remount `mainChatNpc` isn't persisted and
      // it isn't a socket reconnect (`wasReconnecting` is false), so the
      // reconnect re-fire in websocket-service.ts never runs; only the client
      // `welcome-*` bridge bubble survives (via persistence). Detect "Aldric
      // creation in progress but no real Aldric message rendered" and re-request
      // so the backend re-streams the current prompt (the step the session is
      // actually on). Guarded on the absence of a real message so a normal
      // reconnect (chat still in memory) does not double the opener — that path
      // already re-fires with hasHistory=true and gets an empty stream.
      //
      // The `main` room is SHARED across NPCs (switchMainNpc appends the bridge,
      // never clears), so a Tavian-first player carries a persisted Tavian
      // greeting here. Scope the "real message" probe to Aldric's own senderId
      // (streamed openers are `senderId: 'aldric'`; the bridge is excluded by
      // the `welcome-` guard; user replies carry the char id) so retained
      // Tavian/user history doesn't mask a missing opener.
      const resumingAldricOpener =
        sessionEvent.activeNpc === ActiveNPCEnum.ALDRIC &&
        !!sessionEvent.charId &&
        !sessionEvent.characterData?.permanent &&
        !mainRoom?.messages.some(
          (m) =>
            m.senderId === ActiveNPCEnum.ALDRIC &&
            !m.id.startsWith('welcome-') &&
            (m.content?.trim() || m.imageUrl)
        );
      if (resumingAldricOpener) {
        // hasHistory=false → server re-streams the last assistant prompt.
        websocketService.startCharacterCreationSession(false);
      }

      // Clear waiting state, EXCEPT when an inbound stream is expected: (a) a
      // fresh Tavian session awaiting its greeting, or (b) the Aldric opener we
      // just re-requested. Both keep the thinking indicator up.
      // Note: mainRoom is already defined above for NPC room type check
      const hasExistingMessages = mainRoom && mainRoom.messages.length > 0;
      const expectingTavianGreeting =
        sessionEvent.activeNpc === ActiveNPCEnum.TAVIAN && !hasExistingMessages;

      if (resumingAldricOpener) {
        state.setWaitingForResponse(true);
      } else if (!expectingTavianGreeting) {
        state.setWaitingForResponse(false);
      }

      // NOTE: Active game session redirect is now handled by ProtectedRoute at page load.
      // The lobby WebSocket should only connect when there's no active game session.
      // See ProtectedRoute.tsx for the session check flow.

      // Mark lobby:session-state as received and check if both initialization events are ready
      state.setSessionStateReceived(true);
      if (state.lobbyUpdatedReceived) {
        state.setInitialized(true);
      }
      return true;
    }

    case 'lobby:balance-update': {
      // PM balance push (initial on connect + future server-observed changes).
      // Mirrors mobile `LobbyNotifier._onMessage` → `pmBalanceProvider.setBalance`.
      // Powers the in-chat low-balance gate in `ChatPanel.handleSendMessage`.
      const balance = (event as { pmTokenBalance?: number }).pmTokenBalance;
      if (typeof balance === 'number') {
        usePmBalanceStore.getState().setPmBalance(balance);
      }
      return true;
    }

    case 'lobby:characters':
      console.log('[LobbyStore] Received lobby:characters', {
        count: event.characters.length,
        types: event.characters.map((c: unknown) => (c as { type?: string }).type),
        // Log only minimal identifying info, not full objects
        sample: event.characters.slice(0, 3).map((c: unknown) => {
          const char = c as { id?: string; type?: string; name?: string };
          return { id: char.id, type: char.type, name: char.name };
        }),
      });
      state.setLobbyCharacters(event.characters);
      return true;

    default:
      return false;
  }
}
